import { useEffect, useMemo, useState } from "react";
import {
  DEFAULT_PLATFORM_THEME_PREFERENCE,
  isPlatformThemePreference,
} from "@kaleo/account-localization";
import { useHead } from "@unhead/react";
import { useExposedProps, useSidebar, useTheme } from "zudoku/hooks";
import {
  DOCS_ROUTE_LOCALE_COOKIE_NAME,
  DOCS_LOCALES,
  getLocalizedDocsPath,
  parseLocalizedDocsPath,
} from "../lib/docs-routes";
import {
  INTERCOM_OPEN_EVENT,
  getIntercomLanguageOverride,
  getIntercomLauncherHorizontalPadding,
  openIntercomMessenger,
  revalidateIntercomIfDue,
  updateIntercomContext,
  type IntercomContext,
} from "../lib/intercom-client";
import { DOCS_ORIGIN, getDocsMessages } from "../lib/localization";
import {
  DOCS_THEME_BOOTSTRAP_SCRIPT,
  readPlatformThemePreference,
  saveDocsThemePreference,
  shouldSaveDocsThemePreference,
} from "../lib/theme-preference";

export function DocsRuntime() {
  const { location } = useExposedProps();
  const { resolvedTheme, setTheme, theme } = useTheme();
  const sidebarCollapsed = useSidebar((state) => state.isCollapsed);
  const { locale, suffix } = parseLocalizedDocsPath(location.pathname);
  const messages = getDocsMessages(locale);
  const [chatError, setChatError] = useState("");
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof globalThis.window === "undefined"
      ? 0
      : globalThis.window.innerWidth,
  );
  const alternateLinks = suffix
    ? [
        {
          href: `${DOCS_ORIGIN}${getLocalizedDocsPath(locale, suffix)}`,
          rel: "canonical" as const,
        },
        ...DOCS_LOCALES.map((alternateLocale) => ({
          href: `${DOCS_ORIGIN}${getLocalizedDocsPath(alternateLocale, suffix)}`,
          hreflang: alternateLocale,
          rel: "alternate" as const,
        })),
        {
          href: `${DOCS_ORIGIN}${getLocalizedDocsPath("en", suffix)}`,
          hreflang: "x-default",
          rel: "alternate" as const,
        },
      ]
    : [];

  useHead({
    htmlAttrs: { lang: locale },
    link: alternateLinks,
    script: [
      {
        id: "kaleo-theme-bootstrap",
        innerHTML: DOCS_THEME_BOOTSTRAP_SCRIPT,
        tagPriority: "critical",
      },
    ],
  });

  const intercomContext = useMemo<IntercomContext>(
    () => ({
      languageOverride: getIntercomLanguageOverride(locale),
      launcherHorizontalPadding: getIntercomLauncherHorizontalPadding(
        viewportWidth,
        sidebarCollapsed,
      ),
      pagePath: location.pathname,
      themeMode: resolvedTheme === "dark" ? "dark" : "light",
    }),
    [
      locale,
      location.pathname,
      resolvedTheme,
      sidebarCollapsed,
      viewportWidth,
    ],
  );

  useEffect(() => {
    let animationFrame = 0;
    const updateViewportWidth = () => {
      globalThis.cancelAnimationFrame(animationFrame);
      animationFrame = globalThis.requestAnimationFrame(() => {
        setViewportWidth(globalThis.window.innerWidth);
      });
    };

    globalThis.window.addEventListener("resize", updateViewportWidth, {
      passive: true,
    });
    return () => {
      globalThis.window.removeEventListener("resize", updateViewportWidth);
      globalThis.cancelAnimationFrame(animationFrame);
    };
  }, []);

  useEffect(() => {
    if (!suffix) return;

    const secure =
      typeof globalThis.location !== "undefined" &&
      globalThis.location.protocol === "https:"
        ? "; Secure"
        : "";
    globalThis.document.cookie =
      `${DOCS_ROUTE_LOCALE_COOKIE_NAME}=${encodeURIComponent(locale)}; ` +
      `Path=/; SameSite=Lax${secure}`;
  }, [locale, suffix]);

  useEffect(() => {
    if (isPlatformThemePreference(theme)) {
      if (shouldSaveDocsThemePreference(theme, globalThis.document.cookie)) {
        saveDocsThemePreference(theme);
      }
      return;
    }
    if (theme !== undefined) {
      setTheme(
        readPlatformThemePreference(globalThis.document.cookie) ??
          DEFAULT_PLATFORM_THEME_PREFERENCE,
      );
    }
  }, [setTheme, theme]);

  useEffect(() => {
    const synchronizeTheme = () => {
      const preference = readPlatformThemePreference(
        globalThis.document.cookie,
      );
      if (preference && preference !== theme) setTheme(preference);
    };
    const synchronizeVisibleTheme = () => {
      if (globalThis.document.visibilityState === "visible") {
        synchronizeTheme();
      }
    };

    globalThis.window.addEventListener("focus", synchronizeTheme);
    globalThis.window.addEventListener("pageshow", synchronizeTheme);
    globalThis.document.addEventListener(
      "visibilitychange",
      synchronizeVisibleTheme,
    );
    return () => {
      globalThis.window.removeEventListener("focus", synchronizeTheme);
      globalThis.window.removeEventListener("pageshow", synchronizeTheme);
      globalThis.document.removeEventListener(
        "visibilitychange",
        synchronizeVisibleTheme,
      );
    };
  }, [setTheme, theme]);

  useEffect(() => {
    void updateIntercomContext(intercomContext);
  }, [intercomContext]);

  useEffect(() => {
    let mounted = true;
    const handleOpen = () => {
      setChatError("");
      void openIntercomMessenger(intercomContext).then((opened) => {
        if (mounted && !opened) setChatError(messages.chatUnavailable);
      });
    };
    const revalidate = () => {
      if (document.visibilityState === "visible") {
        void revalidateIntercomIfDue(intercomContext);
      }
    };

    globalThis.window.addEventListener(INTERCOM_OPEN_EVENT, handleOpen);
    globalThis.window.addEventListener("focus", revalidate);
    globalThis.window.addEventListener("pageshow", revalidate);
    globalThis.document.addEventListener("visibilitychange", revalidate);

    return () => {
      mounted = false;
      globalThis.window.removeEventListener(INTERCOM_OPEN_EVENT, handleOpen);
      globalThis.window.removeEventListener("focus", revalidate);
      globalThis.window.removeEventListener("pageshow", revalidate);
      globalThis.document.removeEventListener("visibilitychange", revalidate);
    };
  }, [intercomContext, messages.chatUnavailable]);

  return chatError ? (
    <div
      className="kaleo-docs-runtime-alert"
      data-pagefind-ignore="all"
      role="alert"
    >
      {chatError}
    </div>
  ) : null;
}
